import React, { useContext, useEffect } from "react";
import { Container, Row } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DISPENSING_LOG_PATHS } from "../../../../Routes/paths";
import { useDispensingLog } from "../api/useDispensingLog";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import { formatDate } from "common/service/CommonService";

const ViewDispensingLog: React.FC = () => {
    const navigate = useNavigate();
    const breadcrumbContext = useContext(BreadcrumbContext);
    const { id } = useParams();

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Dispensing Log", link: DISPENSING_LOG_PATHS.LIST },
            { text: "View Dispensing Log" },
        ]);

        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);

    const { SelectQuery } = useDispensingLog({
        dispensingid: Number(id),
    });

    const data = SelectQuery.data?.data?.[0];

    if (SelectQuery.isLoading || SelectQuery.isFetching) {
        return (
            <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
                <FullPageLoader />
            </Container>
        );
    }

    if (!data) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="text-center py-5">
                        <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
                        <h4 className="fw-600">Record Not Found</h4>
                        <p className="text-muted">
                            The dispensing log record you are looking for does not exist or has been removed.
                        </p>

                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-700 mb-1">Dispensing Log Details</h3>
                        <p className="text-muted mb-0">
                            Log ID: {data.logid}&nbsp;|&nbsp;
                            Study: {data.studycode}
                        </p>
                    </div>

                </div>

                <div className="d-flex flex-column gap-2">

                    {/* 1 — Study Information */}
                    <SectionCard
                        icon="ri-hospital-line"
                        title="Study Information"
                        subtitle="Protocol and investigator details for this dispensing session."
                    >
                        <Row>
                            <DetailField label="Study Code" value={data.studycode} />
                            <DetailField label="Study Type" value={data.studytypetext} />

                        </Row>
                    </SectionCard>

                    {/* 2 — Investigational Product Details */}
                    <SectionCard
                        icon="ri-capsule-line"
                        title="Investigational Product Details"
                        subtitle="Batch and inventory details of the dispensed product."
                    >
                        <Row>
                            <DetailField label="Product Name" value={data.productname} />
                            <DetailField label="IP Category" value={data.ipcategory} />
                            <DetailField label="Batch/Lot Number" value={data.batchlotno} />
                            <DetailField label="Available Quantity" value={data.availablequantity} />
                            <DetailField
                                label="Expiry Date"
                                value={formatDate(data.expirydate)}
                            />
                        </Row>
                    </SectionCard>

                    {/* 3 — Subject Allocation & Dosing */}
                    <SectionCard
                        icon="ri-user-heart-line"
                        title="Subject Allocation & Dosing"
                        subtitle="Subject range and quantity details for this dispensing session."
                    >
                        <Row>
                            <DetailField label="Start Subject No." value={data.startsubjectno} />
                            <DetailField label="End Subject No." value={data.endsubjectno} />
                            <DetailField label="Quantity per Subject" value={data.quantitypersubject} />
                            <DetailField label="Excluded Subjects" value={data.excludedsubjects} />
                            <DetailField label="Total Quantity Needed" value={data.totalquantityneeded} />
                        </Row>
                    </SectionCard>
                </div>
            </Container>
        </div>
    );
};

export default ViewDispensingLog;